import AxiosInstance from "@/utils/AxiosInstance";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

type UseGetDataReturn<T> = {
  apiData: T | null;
  loading: boolean;
  err: string | null;
  fetchData: () => void;
};

function isAbortError(e: any) {
  return (
    e?.code === "ERR_CANCELED" ||
    e?.name === "CanceledError" ||
    e?.name === "AbortError"
  );
}

export function useGetData<T = any>(
  url: string,
  params: Record<string, any> = {},
  isShowToast = false,
  fetchCondition = true,
  cb: (err: string | null, data?: T) => void = () => {},
): UseGetDataReturn<T> {
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string | null>(null);
  const requestGenRef = useRef(0);
  const cbRef = useRef(cb);
  cbRef.current = cb;
  const urlRef = useRef(url);
  const paramsRef = useRef(params);
  const fetchConditionRef = useRef(fetchCondition);
  urlRef.current = url;
  paramsRef.current = params;
  fetchConditionRef.current = fetchCondition;

  const depKey = JSON.stringify({
    url,
    params,
    fetchCondition: !!fetchCondition,
  });

  const fetchData = useCallback(async () => {
    const currentUrl = urlRef.current;
    const currentParams = paramsRef.current;
    const canFetch = fetchConditionRef.current;
    if (!canFetch || !currentUrl) return;
    try {
      setLoading(true);
      setErr(null);
      const { data } = await AxiosInstance().get<T>(currentUrl, { params: currentParams });
      setApiData(data);
      cbRef.current(null, data);
      if (isShowToast) toast.success("Data fetched successfully !!!");
    } catch (e: any) {
      if (isAbortError(e)) return;
      const errorMsg =
        e?.response?.data?.message || e?.message || "Internal Server Error";
      setErr(errorMsg);
      cbRef.current(errorMsg);
      if (isShowToast) toast.error("Error in data fetching");
    } finally {
      setLoading(false);
    }
  }, [depKey, isShowToast]);

  useEffect(() => {
    if (!fetchCondition || !url) {
      setLoading(false);
      return;
    }

    const requestId = ++requestGenRef.current;
    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const { data } = await AxiosInstance().get<T>(url, {
          params,
          signal: controller.signal,
        } as Parameters<ReturnType<typeof AxiosInstance>["get"]>[1]);
        if (requestId !== requestGenRef.current) return;
        setApiData(data);
        cbRef.current(null, data);
        if (isShowToast) toast.success("Data fetched successfully !!!");
      } catch (e: any) {
        if (isAbortError(e)) return;
        if (requestId !== requestGenRef.current) return;
        const errorMsg =
          e?.response?.data?.message || e?.message || "Internal Server Error";
        setErr(errorMsg);
        cbRef.current(errorMsg);
        if (isShowToast) toast.error("Error in data fetching");
      } finally {
        if (requestId === requestGenRef.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
      requestGenRef.current += 1;
    };
  }, [depKey]);

  return { apiData, loading, err, fetchData };
}
